/**
 * Consultation et ce qui en découle : mesures, tests, questionnaires, schémas corporels, et le
 * suivi post-consultation.
 *
 * Ensembles fermés partagés entre la base (pgEnum), le serveur (zod) et le front (libellés i18n).
 * Règle : valeurs ASCII en snake_case, jamais de libellé humain ici. Ce fichier est réexporté par
 * `../enums.js`, qui reste le seul point d'entrée : personne n'importe un domaine directement.
 */

/**
 * Cycle de vie d'une consultation. `draft` est aussi l'état du brouillon : la V1 avait une table
 * `auto_saves` en plus de la consultation, donc deux vérités pour le même formulaire (audit §1.32).
 * La V2 n'en garde qu'une, mise à jour par `PATCH` partiel.
 */
export const encounterStatuses = ['draft', 'completed'] as const;
export type EncounterStatus = (typeof encounterStatuses)[number];

/**
 * Mesures chiffrées d'une séance, une ligne par code plutôt que cinq colonnes en texte comme en V1.
 * `bmi` est calculé par le serveur à partir de la taille et du poids : il n'est jamais saisi.
 */
export const encounterMeasurementCodes = [
  'height_cm',
  'weight_kg',
  'bmi',
  'eva_before',
  'eva_after',
  'systolic_bp',
  'diastolic_bp',
  'marburg_heart_score',
] as const;
export type EncounterMeasurementCode = (typeof encounterMeasurementCodes)[number];

/** Codes qu'une requête peut poser. Les autres sont dérivés côté serveur. */
export const encounterMeasurementInputCodes = [
  'height_cm',
  'weight_kg',
  'eva_before',
  'eva_after',
  'systolic_bp',
  'diastolic_bp',
  'marburg_heart_score',
] as const;
export type EncounterMeasurementInputCode = (typeof encounterMeasurementInputCodes)[number];

/**
 * Plage de vraisemblance de chaque mesure saisissable, partagée par le schéma de validation et par
 * les curseurs du formulaire. Une seule source : la V1 bornait les valeurs côté client seulement,
 * et stockait le tout en texte.
 */
export interface MeasurementRange {
  readonly min: number;
  readonly max: number;
  /** Vrai quand seule une valeur entière a un sens (score de Marburg). */
  readonly integer?: boolean;
}

export const encounterMeasurementRanges: Readonly<
  Record<EncounterMeasurementInputCode, MeasurementRange>
> = {
  height_cm: { min: 20, max: 280 },
  weight_kg: { min: 0.5, max: 500 },
  eva_before: { min: 0, max: 10 },
  eva_after: { min: 0, max: 10 },
  /** Tension artérielle en millimètres de mercure. */
  systolic_bp: { min: 40, max: 300 },
  diastolic_bp: { min: 20, max: 200 },
  /** Score de Marburg : cinq critères, donc un entier de 0 à 5. */
  marburg_heart_score: { min: 0, max: 5, integer: true },
};

/** Résultat d'un test clinique. `not_performed` existe pour distinguer « non fait » de « négatif ». */
export const clinicalTestOutcomes = [
  'positive',
  'negative',
  'inconclusive',
  'not_performed',
] as const;
export type ClinicalTestOutcome = (typeof clinicalTestOutcomes)[number];

/** Un questionnaire est soit un instrument du catalogue `@osteopro/clinical`, soit un modèle du cabinet. */
export const questionnaireResponseKinds = ['standard', 'custom'] as const;
export type QuestionnaireResponseKind = (typeof questionnaireResponseKinds)[number];

/**
 * Instruments intégrés. La liste doit rester identique à `questionnaireCodes` de `@osteopro/clinical` ;
 * le serveur le vérifie à la compilation (`encounters-questionnaires.service.ts`). Elle vit ici parce
 * que le contrat réseau est partagé avec le front, et que `@osteopro/clinical` dépend déjà de ce paquet.
 */
export const standardQuestionnaireCodes = [
  'dn4',
  'paindetect',
  'csi',
  'orebro',
  'epworth',
  'audit_c',
  'hads',
  'walton',
] as const;
export type StandardQuestionnaireCode = (typeof standardQuestionnaireCodes)[number];

/**
 * Vues du schéma corporel. La V1 mélangeait `front/back/leftSide/rightSide` et l'ancien
 * `face/profil/dos` (audit §1.8) : une seule forme, courte et ASCII.
 */
export const bodyDiagramViews = ['front', 'back', 'left', 'right'] as const;
export type BodyDiagramView = (typeof bodyDiagramViews)[number];

/**
 * Suivi post-consultation.
 *
 * Trois types de question seulement, ceux que `docs/SCREENS.md` § « Section 5 — Suivi patient »
 * décrit : du texte libre, une échelle de zéro à dix, une réponse oui-non. C'est un questionnaire
 * de nouvelles, pas un instrument clinique — ceux-là vivent dans `@osteopro/clinical`.
 */
export const followUpQuestionTypes = ['text', 'scale', 'boolean'] as const;
export type FollowUpQuestionType = (typeof followUpQuestionTypes)[number];

/**
 * Cycle de vie d'un suivi. `scheduled` tant que la date d'envoi n'est pas atteinte, `sent` une fois
 * l'email parti, `answered` quand le patient a répondu, `cancelled` quand le praticien retire le
 * suivi ou que le dossier n'a plus d'adresse. Ce sont les trois badges de la section « Réponse du
 * patient », plus l'état d'abandon.
 */
export const followUpStatuses = ['scheduled', 'sent', 'answered', 'cancelled'] as const;
export type FollowUpStatus = (typeof followUpStatuses)[number];

/** Bornes du délai d'envoi, en jours. Reprises telles quelles de la spécification. */
export const FOLLOW_UP_MIN_DELAY_DAYS = 1;
export const FOLLOW_UP_MAX_DELAY_DAYS = 90;

/**
 * Délai proposé par défaut : **trois jours**, décidé par les associés le 20 septembre 2026. La V1
 * proposait une semaine ; trois jours tombent pendant que la séance compte encore, quand le
 * patient sait dire si la douleur est revenue.
 *
 * C'est un **défaut**, pas une borne : le cabinet le change dans son modèle de suivi, et le
 * praticien le change encore sur chaque consultation. La borne basse reste
 * `FOLLOW_UP_MIN_DELAY_DAYS`.
 */
export const FOLLOW_UP_DEFAULT_DELAY_DAYS = 3;

/**
 * Plancher **structurel** du délai, celui que le contrat réseau déclare et que la contrainte de
 * base autorise. Zéro n'est pas une absurdité — c'est « au prochain passage de la tâche » —, et la
 * recette automatisée s'en sert. La **règle du produit**, elle, reste un jour : elle vit dans
 * `FOLLOW_UP_MIN_DELAY_DAYS` côté serveur, où la configuration peut la resserrer, jamais dans la
 * forme du corps. Le contrat décrit la forme, la configuration resserre la borne.
 */
export const FOLLOW_UP_FLOOR_DELAY_DAYS = 0;

/**
 * Note d'un avis patient. Cinq étoiles renvoient vers l'avis public du cabinet, moins ouvrent un
 * retour privé qui ne sort pas du cabinet : c'est le filtre décrit par `docs/SCREENS.md`.
 */
export const REVIEW_MIN_RATING = 1;
export const REVIEW_MAX_RATING = 5;
/** Au-dessous de ce seuil, le retour reste privé et n'est jamais redirigé vers un avis public. */
export const REVIEW_PUBLIC_THRESHOLD = 5;

/** Types de champ d'un questionnaire personnalisé, repris à l'identique de la V1. */
export const questionnaireFieldTypes = [
  'text',
  'textarea',
  'number',
  'select',
  'radio',
  'checkbox',
  'toggle',
  'date',
  'scale',
] as const;
export type QuestionnaireFieldType = (typeof questionnaireFieldTypes)[number];

/* -------------------------------------------------------------------------- */
/* Courriers médicaux                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Nature d'un courrier. Reprise de la V1, moins son fourre-tout : `other` y servait à tout, sans
 * qu'aucun écran ne sache quoi en faire — un courrier sans nature n'existe pas.
 */
export const medicalLetterTypes = ['referral', 'certificate', 'report'] as const;
export type MedicalLetterType = (typeof medicalLetterTypes)[number];

/** À qui le courrier s'adresse. Détermine le ton de la rédaction et l'en-tête de l'impression. */
export const medicalLetterRecipientTypes = ['doctor', 'specialist', 'lab', 'patient'] as const;
export type MedicalLetterRecipientType = (typeof medicalLetterRecipientTypes)[number];

/**
 * Cycle de vie d'un courrier, et il ne se remonte pas : un brouillon se finalise, un courrier
 * finalisé s'envoie, et rien ne redevient brouillon. La V1 laissait le client poser le statut
 * qu'il voulait — son bouton « Sauvegarder » rétrogradait en brouillon un courrier déjà envoyé.
 */
export const medicalLetterStatuses = ['draft', 'finalized', 'sent'] as const;
export type MedicalLetterStatus = (typeof medicalLetterStatuses)[number];

/**
 * Ce qui a produit le corps du courrier : la main du praticien, ou une génération assistée qu'il a
 * relue. La trace reste sur la ligne — modèle et instant —, jamais la charge utile envoyée au
 * fournisseur : la V1 gardait le prompt entier en base, c'est-à-dire une seconde copie de
 * l'identité et du récit clinique, hors du dossier et invisible à l'export RGPD.
 */
export const medicalLetterSources = ['manual', 'ai'] as const;
export type MedicalLetterSource = (typeof medicalLetterSources)[number];

/** Bornes de saisie d'un courrier. Le corps est long : c'est une lettre, pas un champ de formulaire. */
export const MEDICAL_LETTER_SUBJECT_MAX = 200;
export const MEDICAL_LETTER_BODY_MAX = 20_000;
